/**
 * SILENT ACCESS-TOKEN RENEWAL — `prompt=none` authorize against the live
 * Central SSO session, in a hidden frame, with the SPA left mounted.
 *
 * ── WHAT THIS REPLACES ───────────────────────────────────────────────────
 *
 * BeHeard is a PUBLIC OIDC client that stores NO refresh token
 * (`hooks/useCentralAuth.ts` sets `refreshToken: ''`) and keeps the access token
 * in memory only. Renewal has always been "re-run OIDC against the Central
 * session cookie" — that part of the architecture is deliberate and is kept.
 *
 * What was wrong was HOW. The 15-minute access-token expiry went through
 * `centralLogout()` → `AuthGuard` → `/login` → `initiateCentralLogin()` →
 * `window.location.assign(...)`. That is a FULL-PAGE round trip: the SPA
 * unmounts, in-page state and any unsaved work are destroyed, and it travels the
 * SAME path as a real session end, so a routine expiry and a genuine
 * SESSION_EXPIRED were indistinguishable to the user — every fifteen minutes.
 *
 * The contract is: *access token expires → silent refresh → the user stays in
 * the app*. This module is that silent refresh.
 *
 * ── THE MECHANISM ────────────────────────────────────────────────────────
 *
 *   1. Generate a fresh PKCE pair (S256) and a `state` carrying
 *      SILENT_STATE_PREFIX. NOTHING is written to sessionStorage: the verifier
 *      lives in this function's closure. Writing it would clobber an
 *      interactive login flow's verifier under the same key.
 *   2. Point a hidden iframe at Central's `/api/oidc/authorize` with
 *      `prompt=none` and BeHeard's REGISTERED redirect URI.
 *   3. The browser carries the `central_sid` cookie on that frame navigation, so
 *      Central answers from the live SSO session and redirects back to
 *      BeHeard's own origin with `?code=`.
 *   4. That page is BeHeard's, so `auth/silentCallbackBridge.ts` runs in the
 *      frame, posts the parameters to this document, and does NOT boot a second
 *      copy of the SPA.
 *   5. The parent exchanges the code at Central's token endpoint (PKCE, no
 *      cookie needed) and writes the new access token into Redux.
 *
 * NO BACKEND ROUTE IS INVOLVED, on either side. The authorize call and the token
 * exchange both go browser-direct to Central, exactly as the interactive login
 * already does. BeHeard's own backend still mounts no `/api/auth/*` router, and
 * the dead same-origin `POST /api/auth/central/refresh` that could only ever
 * 404 is gone (see `store/__tests__/refreshPathPremise.test.ts`).
 *
 * ── THE THREE OUTCOMES, AND WHY THE MIDDLE ONE MATTERS MOST ──────────────
 *
 *   ok                   a new access token. The SPA never unmounted.
 *   SESSION_OVER         there is NO live Central session. Only this ends the
 *                        session locally and sends the user to Central login.
 *   CENTRAL_UNAVAILABLE  the attempt could not COMPLETE. Keep the credential,
 *                        keep the session, never login — the caller surfaces a
 *                        retryable failure.
 *
 * A refusal and a non-completion must never be confused, because the hidden
 * frame has failure modes that say nothing whatsoever about the user's session:
 * blocked third-party storage, a partitioned cookie jar, `X-Frame-Options` on an
 * intermediate document, a frame that never answers, a timeout. Logging a user
 * out because their browser blocked an iframe would be a worse bug than the
 * full-page round trip this module removes.
 *
 * ── THE PART THAT IS NOT GUESSWORK, AND THE PART THAT IS ─────────────────
 *
 * Central's `/api/oidc/authorize` does NOT implement `prompt` today
 * (`backend/src/routes/api/oidc.ts`: the parameter is never read, and a request
 * with no session is answered with `res.redirect('/auth/login?continue=…')` —
 * Central's own login PAGE — not with an `error=login_required` redirect back to
 * the RP). So on the Central that is deployed right now, a dead session makes
 * the hidden frame load a page that cannot answer us, and we observe a TIMEOUT
 * rather than a refusal.
 *
 * A timeout alone must never end a session, so the terminal verdict is reached a
 * second way: ask Central directly whether the SSO session is alive, with a
 * cookie-bearing request to an endpoint that answers 200 or 401 and nothing
 * else (`/api/oidc/session/workspaces`, `sessionAuth` + `requireSession`).
 *
 *   explicit `login_required` / `interaction_required` / `consent_required`
 *       -> SESSION_OVER                        (spec path; works the day
 *                                               Central implements `prompt`)
 *   inconclusive + probe says 401              -> SESSION_OVER
 *   inconclusive + probe says 200 / unknown    -> CENTRAL_UNAVAILABLE
 *   inconclusive + NOT same-site with Central  -> CENTRAL_UNAVAILABLE, always
 *
 * That last line is the third-party-cookie guard. A 401 from the probe is only
 * evidence about the SESSION when the cookie could have been SENT. BeHeard and
 * Central are same-site in every deployed configuration (beheard.roilevelup.com
 * / central.roilevelup.com, beheard-staging / central-staging, and
 * localhost:5173 / localhost:3100 in development — `SameSite` is scoped to the
 * registrable domain and ignores the port), so the cookie is first-party and
 * neither ITP nor Chrome's third-party phase-out applies. Where that is NOT true
 * the probe's refusal is discarded and the attempt degrades to "unavailable" —
 * never to a logout.
 */

import { generatePKCE, buildAuthorizationUrl } from '@levelup/central-auth';
import {
  OIDC_ENDPOINTS,
  OIDC_CLIENT_ID,
  OIDC_REDIRECT_URI,
  CENTRAL_URL,
} from '../config/centralAuth';
import { updateCentralAccessToken } from '../store/slices/centralAuthSlice';
import {
  SILENT_MESSAGE_SOURCE,
  SILENT_STATE_PREFIX,
  type SilentCallbackMessage,
} from './silentAuthorizeProtocol';

/**
 * Minimal dispatch shape. Deliberately loose in the parameter position so a base
 * query's `api.dispatch`, a hook's dispatch and the store's own dispatch are all
 * assignable without this module importing the store's types.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

// ──────────────────────────────────────────────────────────────────────────
// Outcomes
// ──────────────────────────────────────────────────────────────────────────

/** What one silent authorize attempt concluded. */
export type SilentAuthorizeOutcome =
  | { ok: true; accessToken: string; expiresIn: number }
  /** There is no live Central session. The session really is over. */
  | { ok: false; disposition: 'SESSION_OVER'; reason: string }
  /** The attempt could not complete. Says NOTHING about the session. */
  | { ok: false; disposition: 'CENTRAL_UNAVAILABLE'; reason: string };

/** What a caller of the shared single-flight renewal gets back. */
export type RenewalOutcome =
  | { ok: true }
  | { ok: false; disposition: 'SESSION_OVER' | 'CENTRAL_UNAVAILABLE'; reason: string };

/**
 * OIDC `error` values that MEAN "I will not do this without talking to the
 * user", i.e. there is no usable session. RFC 6749 §4.1.2.1 + OIDC Core §3.1.2.6.
 * These — and ONLY these — are terminal.
 */
const REFUSAL_ERRORS: readonly string[] = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
];

/** How long a hidden-frame attempt may take before it is called inconclusive. */
export const SILENT_FRAME_TIMEOUT_MS = 8_000;

// ──────────────────────────────────────────────────────────────────────────
// Same-site analysis (pure)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Two-label public suffixes we refuse to treat as a registrable domain.
 * Without this, `app.co.uk` and `evil.co.uk` would look same-site.
 *
 * This is a deliberately SHORT list: the function is only ever used to decide
 * whether a refusal may be trusted, and the safe answer when unsure is "not
 * same-site", which can only ever downgrade a logout to a retry.
 */
const TWO_LABEL_SUFFIXES: readonly string[] = [
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'co.jp', 'or.jp', 'ne.jp',
  'com.au', 'net.au', 'org.au', 'co.nz', 'com.br', 'com.mx', 'co.in', 'co.za',
  'co.kr', 'com.sg', 'com.tr', 'com.cn', 'com.hk', 'com.tw',
];

/**
 * The registrable domain of a host, for a SameSite comparison.
 * `localhost`, a bare hostname and an IP literal are their own site.
 */
export function registrableSite(host: string): string {
  const h = host.trim().toLowerCase().replace(/\.$/, '');
  if (h === '') return '';
  // IPv6 literal, IPv4 literal, or a single-label host: host-only.
  if (h.indexOf(':') !== -1) return h;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return h;
  const labels = h.split('.');
  if (labels.length <= 2) return h;
  const lastTwo = labels.slice(-2).join('.');
  if (TWO_LABEL_SUFFIXES.indexOf(lastTwo) !== -1) return labels.slice(-3).join('.');
  return lastTwo;
}

/**
 * True when a cookie Central sets is FIRST-PARTY from this app's origin, so a
 * cookie-bearing request to Central carries the SSO session and a refusal can be
 * believed.
 *
 * Scheme is part of the test: a `Secure` cookie is not sent to `http://`, so a
 * mixed-scheme pair must not be trusted either.
 */
export function isSameSiteWithCentral(appOrigin: string, centralUrl: string): boolean {
  try {
    const app = new URL(appOrigin);
    const central = new URL(centralUrl);
    if (!app.hostname || !central.hostname) return false;
    if (app.protocol !== central.protocol) return false;
    const appSite = registrableSite(app.hostname);
    return appSite !== '' && appSite === registrableSite(central.hostname);
  } catch {
    return false;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// The attempt, with every browser interaction behind an injectable seam
// ──────────────────────────────────────────────────────────────────────────

/** What came back from the hidden frame. */
export type SilentFrameAnswer =
  /** Central redirected to our redirect URI; these are its parameters. */
  | { kind: 'params'; params: Record<string, string> }
  /** The round trip never produced an answer we can read. */
  | { kind: 'inconclusive'; reason: string };

/** The result of exchanging an authorization code. */
export type SilentExchangeResult =
  | { ok: true; accessToken: string; expiresIn: number }
  | { ok: false; reason: string };

/** Is there a live Central SSO session for this browser, right now? */
export type SessionLiveness = 'ALIVE' | 'DEAD' | 'UNKNOWN';

export interface SilentAuthorizeDeps {
  /** Fresh PKCE pair. */
  pkce: () => Promise<{ codeVerifier: string; codeChallenge: string }>;
  /** A fresh, SILENT-prefixed `state`. */
  newState: () => string;
  /** The `prompt=none` authorize URL for this attempt. */
  authorizeUrl: (p: { codeChallenge: string; state: string }) => string;
  /** Run ONE hidden-frame round trip. */
  runFrame: (url: string, ctx: { state: string; timeoutMs: number }) => Promise<SilentFrameAnswer>;
  /** Exchange the code for an access token. */
  exchange: (code: string, codeVerifier: string) => Promise<SilentExchangeResult>;
  /** Ask Central whether the SSO session is alive (cookie-bearing). */
  probeSession: () => Promise<SessionLiveness>;
  /** Whether a refusal from `probeSession` may be trusted at all. */
  cookiesAreFirstParty: boolean;
  timeoutMs: number;
}

function sessionOver(reason: string): SilentAuthorizeOutcome {
  return { ok: false, disposition: 'SESSION_OVER', reason };
}

function unavailable(reason: string): SilentAuthorizeOutcome {
  return { ok: false, disposition: 'CENTRAL_UNAVAILABLE', reason };
}

/**
 * Decide what an attempt that produced no readable answer means.
 *
 * This is the whole third-party-cookie guard, and it fails in the direction that
 * cannot hurt: anything short of "Central itself told us there is no session,
 * over a request whose cookie we know could be sent" is an outage.
 */
async function resolveInconclusive(
  deps: SilentAuthorizeDeps,
  reason: string,
): Promise<SilentAuthorizeOutcome> {
  if (!deps.cookiesAreFirstParty) {
    return unavailable(
      `${reason}; this app and Central are not same-site, so a missing session cannot be told apart from a blocked cookie`,
    );
  }

  let liveness: SessionLiveness;
  try {
    liveness = await deps.probeSession();
  } catch {
    liveness = 'UNKNOWN';
  }

  if (liveness === 'DEAD') {
    return sessionOver(`${reason}; Central reports no live SSO session`);
  }
  return unavailable(
    liveness === 'ALIVE'
      ? `${reason}; Central reports the SSO session is still alive, so this is the frame failing, not the session`
      : `${reason}; session liveness could not be established`,
  );
}

/**
 * ONE silent authorize attempt. Pure with respect to the browser: every side
 * effect arrives through `deps`, which is what makes the decision table above
 * testable in a package with no DOM.
 */
export async function performSilentAuthorize(
  deps: SilentAuthorizeDeps,
): Promise<SilentAuthorizeOutcome> {
  let pair: { codeVerifier: string; codeChallenge: string };
  try {
    pair = await deps.pkce();
  } catch (err) {
    // No WebCrypto, or a hostile environment. Not a session verdict.
    return unavailable(`PKCE could not be generated: ${describe(err)}`);
  }

  const state = deps.newState();

  let answer: SilentFrameAnswer;
  try {
    answer = await deps.runFrame(deps.authorizeUrl({ codeChallenge: pair.codeChallenge, state }), {
      state,
      timeoutMs: deps.timeoutMs,
    });
  } catch (err) {
    return resolveInconclusive(deps, `the silent frame threw: ${describe(err)}`);
  }

  if (answer.kind === 'inconclusive') {
    return resolveInconclusive(deps, answer.reason);
  }

  const error = answer.params['error'];
  if (error !== undefined && error !== '') {
    // THE terminal signal: Central refused to answer without the user.
    if (REFUSAL_ERRORS.indexOf(error) !== -1) {
      return sessionOver(`Central refused a silent authorize: ${error}`);
    }
    // Everything else — server_error, temporarily_unavailable, invalid_request,
    // invalid_client, unsupported_response_type, access_denied — is a FAILURE of
    // the attempt, not a verdict on the session. A misconfigured client must not
    // be able to log the whole user base out.
    return unavailable(`authorize returned a non-refusal error: ${error}`);
  }

  const code = answer.params['code'];
  if (code === undefined || code === '') {
    return resolveInconclusive(deps, 'authorize answered with neither a code nor an error');
  }

  let exchanged: SilentExchangeResult;
  try {
    exchanged = await deps.exchange(code, pair.codeVerifier);
  } catch (err) {
    return unavailable(`token exchange threw: ${describe(err)}`);
  }

  if (exchanged.ok) {
    return { ok: true, accessToken: exchanged.accessToken, expiresIn: exchanged.expiresIn };
  }

  // A code Central issued SECONDS ago failing to exchange is a transport or
  // service problem (or a replayed code), never proof the session is finished.
  return unavailable(`token exchange failed: ${exchanged.reason}`);
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ──────────────────────────────────────────────────────────────────────────
// The browser implementation of the seams
// ──────────────────────────────────────────────────────────────────────────

function randomStateSuffix(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/** The `prompt=none` authorize URL, built on the SHARED authorization builder. */
export function buildSilentAuthorizeUrl(p: { codeChallenge: string; state: string }): string {
  const url = new URL(
    buildAuthorizationUrl({
      authorizeEndpoint: OIDC_ENDPOINTS.authorize,
      clientId: OIDC_CLIENT_ID,
      redirectUri: OIDC_REDIRECT_URI,
      codeChallenge: p.codeChallenge,
      state: p.state,
      scope: 'openid profile email',
    }),
  );
  // The whole point: never show the user anything. An OP that honours it answers
  // with an error redirect instead of a login page.
  url.searchParams.set('prompt', 'none');
  return url.toString();
}

/**
 * Open a hidden frame at `url` and resolve with whatever BeHeard's own callback
 * page posts back, or `inconclusive` when nothing readable arrives in time.
 *
 * There is deliberately NO `load`-event shortcut. A frame `load` fires for
 * `about:blank`, for an `X-Frame-Options`-blocked error page and for Central's
 * own login page alike, and treating it as "no answer coming" would turn a
 * perfectly good renewal into an outage in any browser that fires it early. The
 * cost of waiting for the timeout is paid only on the terminal path.
 */
function browserRunFrame(
  url: string,
  ctx: { state: string; timeoutMs: number },
): Promise<SilentFrameAnswer> {
  return new Promise<SilentFrameAnswer>((resolve) => {
    let settled = false;
    let frame: HTMLIFrameElement | null = null;
    let timer = 0;

    const cleanup = () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
      frame = null;
    };

    const settle = (answer: SilentFrameAnswer) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(answer);
    };

    function onMessage(event: MessageEvent): void {
      // Same-origin only: the callback page is OURS. Nothing Central (or anyone
      // else) posts cross-origin is read.
      if (event.origin !== window.location.origin) return;
      const data = event.data as SilentCallbackMessage | null;
      if (!data || typeof data !== 'object') return;
      if (data.source !== SILENT_MESSAGE_SOURCE) return;
      // An answer from an abandoned earlier attempt must not satisfy this one.
      if (data.state !== ctx.state) return;
      settle({ kind: 'params', params: data.params ?? {} });
    }

    window.addEventListener('message', onMessage);
    timer = window.setTimeout(
      () => settle({ kind: 'inconclusive', reason: 'the silent frame did not answer in time' }),
      ctx.timeoutMs,
    );

    try {
      frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.title = 'Central silent sign-in';
      frame.style.cssText =
        'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;border:0;visibility:hidden;';
      // `src` BEFORE insertion, so the frame does not first load about:blank.
      frame.src = url;
      document.body.appendChild(frame);
    } catch (err) {
      settle({ kind: 'inconclusive', reason: `the silent frame could not be opened: ${describe(err)}` });
    }
  });
}

/** PKCE code exchange, browser-direct to Central. No cookie, no backend. */
async function browserExchange(code: string, codeVerifier: string): Promise<SilentExchangeResult> {
  const response = await fetch(OIDC_ENDPOINTS.token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: OIDC_CLIENT_ID,
      code,
      code_verifier: codeVerifier,
      redirect_uri: OIDC_REDIRECT_URI,
    }),
  });

  if (!response.ok) return { ok: false, reason: `token endpoint answered ${response.status}` };

  const body = (await response.json().catch(() => undefined)) as
    | { access_token?: string; expires_in?: number }
    | undefined;

  if (!body || typeof body.access_token !== 'string' || body.access_token === '') {
    return { ok: false, reason: 'token response carried no access_token' };
  }
  // Central always sends `expires_in`; the fallback is the real 15-minute
  // access-token life rather than an optimistic hour.
  return {
    ok: true,
    accessToken: body.access_token,
    expiresIn: typeof body.expires_in === 'number' ? body.expires_in : 900,
  };
}

/**
 * Ask Central whether this browser still has a live SSO session.
 *
 * `GET /api/oidc/session/workspaces` is `sessionAuth` + `requireSession`, so it
 * answers 200 with a session and 401 without, and it is read-only. Classified on
 * the STATUS alone and never through `centralFailureContract`: its 401 body is
 * `{ ok: false, error: 'Authentication required' }`, which is prose rather than
 * a Central vocabulary code, and the shared classifier would rightly call that
 * uninterpretable. Here the status IS the whole answer.
 */
async function browserProbeSession(): Promise<SessionLiveness> {
  try {
    const response = await fetch(OIDC_ENDPOINTS.workspaces, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) return 'ALIVE';
    if (response.status === 401 || response.status === 403) return 'DEAD';
    return 'UNKNOWN';
  } catch {
    return 'UNKNOWN';
  }
}

/** The production seams. */
export function browserSilentAuthorizeDeps(): SilentAuthorizeDeps {
  return {
    pkce: generatePKCE,
    newState: () => `${SILENT_STATE_PREFIX}${randomStateSuffix()}`,
    authorizeUrl: buildSilentAuthorizeUrl,
    runFrame: browserRunFrame,
    exchange: browserExchange,
    probeSession: browserProbeSession,
    cookiesAreFirstParty: isSameSiteWithCentral(
      typeof window !== 'undefined' && window.location ? window.location.origin : '',
      CENTRAL_URL,
    ),
    timeoutMs: SILENT_FRAME_TIMEOUT_MS,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Single flight
// ──────────────────────────────────────────────────────────────────────────
//
// A SHARED PROMISE, not a mutex. The difference is the whole point:
//
//   a mutex SERIALISES — twenty 401s would run twenty authorize round trips,
//   one after another, each opening a frame and burning an authorization code,
//   and the nineteen after the first are pure waste (and, against an OP that
//   rate-limits `/authorize`, actively harmful);
//
//   a shared promise DEDUPLICATES — the first caller starts the one attempt and
//   every later caller awaits that same promise, so twenty 401s produce exactly
//   ONE authorize and twenty retries.
//
// The token write happens INSIDE the shared body, so it also happens exactly
// once however many callers are waiting.
//
// Nothing here is sticky. A CENTRAL_UNAVAILABLE outcome leaves no latch behind:
// the next 401 may well renew fine, because a blocked frame, a partitioned
// cookie jar or a flaky network are all conditions that come and go. The old
// `refreshEndpointAbsent` bit was legitimate only because it recorded a
// DEPLOYMENT fact (this origin hosts no refresh route) and that fact is gone
// along with the route.

let inFlight: Promise<SilentAuthorizeOutcome> | null = null;
let attempt: (() => Promise<SilentAuthorizeOutcome>) | null = null;

/** Test seam: script the attempt without a DOM. */
export function __setSilentAuthorizeForTests(
  fn: (() => Promise<SilentAuthorizeOutcome>) | null,
): void {
  attempt = fn;
}

/** Test seam: drop any in-flight attempt between cases. */
export function __resetSilentAuthorizeState(): void {
  inFlight = null;
  attempt = null;
}

function runAttempt(): Promise<SilentAuthorizeOutcome> {
  if (attempt) return attempt();
  return performSilentAuthorize(browserSilentAuthorizeDeps());
}

/**
 * Renew the access token silently, at most ONCE at a time across the whole app.
 *
 * Every Central perimeter funnels through here — `store/api.ts` for RTK Query
 * traffic and `auth/reportCentralFailure.ts` for the raw-`fetch` Central reads —
 * so a polling notification read and twenty page queries that all expire in the
 * same instant still produce one authorize and one token write.
 */
export function renewAccessTokenSilently(dispatch: Dispatch): Promise<RenewalOutcome> {
  const existing = inFlight;
  if (existing) return existing.then(toRenewalOutcome);

  const mine = (async (): Promise<SilentAuthorizeOutcome> => {
    let outcome: SilentAuthorizeOutcome;
    try {
      outcome = await runAttempt();
    } catch (err) {
      // This promise is shared by every waiter, so it must NEVER reject: one
      // unexpected throw would otherwise become an unhandled rejection in
      // twenty call sites. An unexplained throw is an outage, never a verdict.
      outcome = unavailable(`the silent renewal threw: ${describe(err)}`);
    }
    if (outcome.ok) {
      // ONE write, inside the shared attempt, whoever is waiting on it.
      dispatch(
        updateCentralAccessToken({
          accessToken: outcome.accessToken,
          expiresIn: outcome.expiresIn,
        }),
      );
    } else {
      console.warn(`[silent-auth] ${outcome.disposition}: ${outcome.reason}`);
    }
    return outcome;
  })();

  inFlight = mine;
  // Creator-only release, guarded by identity, so a late waiter can never clear
  // a slot it did not create and let a second attempt start mid-flight.
  void mine
    .catch(() => undefined)
    .finally(() => {
      if (inFlight === mine) inFlight = null;
    });

  return mine.then(toRenewalOutcome);
}

function toRenewalOutcome(outcome: SilentAuthorizeOutcome): RenewalOutcome {
  return outcome.ok ? { ok: true } : { ok: false, disposition: outcome.disposition, reason: outcome.reason };
}
